/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2024 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getVariantChildrenDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import { ImageModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@stoneage/App/Store/Reducers";
import { getBillToCustomerNumber, trackAsImpersonation, trackGaBomCtaEvent } from "@stoneage/App/Utilities/DataLayer";
import LazyImage, { LazyImageProps } from "@stoneage/MobiusOverrides/LazyImage";
import * as React from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";

const enum fields {
    buttonText = "buttonText",
    productHasBom = "productHasBom",
    parentAndHasChildBom = "parentAndHasChildBom",
    childAndHasSiblingBom = "childAndHasSiblingBom",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.buttonText]: string;
        [fields.productHasBom]: string;
        [fields.parentAndHasChildBom]: string;
        [fields.childAndHasSiblingBom]: string;
    };
}

type Props = OwnProps & HasProduct & ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: CustomAppState, ownProps: HasProduct) => ({
    bomImageName: (getSettingsCollection(state) as any)?.customSettings?.billOfMaterialImageName,
    generalBillOfMaterialImage: (getSettingsCollection(state) as any)?.customSettings?.generalBillOfMaterialImage,
    configurationCompleted: state.pages.productDetails.configurationCompleted,
    variantSelectionCompleted: state.pages.productDetails.variantSelectionCompleted,
    variantChildren: getVariantChildrenDataView(state, ownProps.product.id).value!,
    customerNumber: getBillToCustomerNumber(state),
    impersonation: trackAsImpersonation(state),
    varianChildrenById: state.pages.productDetails.productInfosById,
    bomCtaEvent: (state as CustomAppState).xGoogleAnalytics.bomCtaEvent,
});

export interface ViewBomStyles {
    container?: InjectableCss;
    description?: TypographyPresentationProps;
    imageWrapper?: InjectableCss;
    buttonWrapper?: InjectableCss;
    viewButton?: ButtonPresentationProps;
    image?: LazyImageProps;
    clickableImg?: ClickablePresentationProps;
}

export const viewBomStyles: ViewBomStyles = {};

const styles = viewBomStyles;

const ViewBom = ({
    product,
    bomImageName,
    fields: { buttonText, productHasBom, parentAndHasChildBom,  childAndHasSiblingBom },

    variantChildren,
    configurationCompleted,
    variantSelectionCompleted,
    customerNumber,
    impersonation,
    bomCtaEvent,
    generalBillOfMaterialImage,
}: Props) => {
    const dispatch = useDispatch();
    let isButtonDisabled = false;
    let descriptionText = productHasBom;

    if (!product) {
        return null;
    }

    const productBomImage = product?.images?.find(img => img.name === bomImageName);
    if (productBomImage) {
        descriptionText = productHasBom;
        isButtonDisabled = false;
    } else if (
        !product.isVariantParent &&
        product.variantTraits?.length &&
        product.properties["otherChildrenHasBomImage"]?.toLowerCase() === "true"
    ) {
        descriptionText = childAndHasSiblingBom;
        isButtonDisabled = true;
    }

    if (
        product.isVariantParent &&
        !variantSelectionCompleted &&
        !configurationCompleted &&
        variantChildren?.some(child => child?.images?.some(img => img.name === bomImageName))
    ) {
        descriptionText = parentAndHasChildBom;
        isButtonDisabled = true;
    }

    const image =
        generalBillOfMaterialImage &&
        ({
            id: `${product.id}_bomImage`,
            imageType: "Static",
            imageAltText: product.imageAltText,
            largeImagePath: generalBillOfMaterialImage,
            mediumImagePath: generalBillOfMaterialImage,
            smallImagePath: generalBillOfMaterialImage,
        } as ImageModel);

    const img = isButtonDisabled ? image : productBomImage;

    if (!isButtonDisabled && !img) {
        return null;
    }

    const addShowBOMParamAsFirst = (url: string) => {
        const urlObj = new URL(url);
        const currentParams = urlObj.searchParams;

        // Create a new URL, starting with "showbom=true"
        const newParams = new URLSearchParams();
        newParams.set("showbom", "true");

        for (const [key, value] of currentParams) {
            if (key !== "showbom") {
                newParams.append(key, value);
            }
        }
        urlObj.search = newParams.toString();
        return urlObj.toString();
    };

    const viewBomHandler = () => {
        // track GA4 event
        bomCtaEvent && trackGaBomCtaEvent(product.productNumber, impersonation, customerNumber);
        const currentUrl = window.location.href;
        const newUrl = addShowBOMParamAsFirst(currentUrl);
        window.history.replaceState({}, document.title, newUrl);
        dispatch({ type: "xCommon/update", data: { key: "isBomView", value: true } });
    };

    return (
        <StyledWrapper {...styles.container}>
            {img && (
                <Clickable {...styles.clickableImg} onClick={viewBomHandler} disabled={isButtonDisabled}>
                    <StyledWrapper {...styles.imageWrapper}>
                        <LazyImage
                            altText={img.imageAltText}
                            data-test-selector="productDetails_bomImage"
                            key={img.id}
                            {...styles.image}
                            src={img.mediumImagePath || img.largeImagePath}
                        />
                    </StyledWrapper>
                </Clickable>
            )}
            {descriptionText && <Typography {...styles.description}>{descriptionText}</Typography>}
            <StyledWrapper {...styles.buttonWrapper}>
                <Button disabled={isButtonDisabled} {...styles.viewButton} onClick={viewBomHandler}>
                    {buttonText}
                </Button>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const widgetModule: WidgetModule = {
    component: withProduct(connect(mapStateToProps)(ViewBom)),
    definition: {
        displayName: "View BOM",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.productHasBom,
                displayName: "Product Has BOM Available Text",
                editorTemplate: "TextField",
                defaultValue: "View BOM",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.parentAndHasChildBom,
                displayName: "Parent Has a child with BOM Available Text",
                editorTemplate: "TextField",
                defaultValue: "Selection Required",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.childAndHasSiblingBom,
                displayName: "Child Product and has a sibling with BOM",
                editorTemplate: "TextField",
                defaultValue: "No Associated BOM available",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.buttonText,
                displayName: "Button Text",
                editorTemplate: "TextField",
                defaultValue: "View BOM",
                fieldType: "General",
                tab: basicTab,
                isRequired: true,
            },

        ],
    },
};

export default widgetModule;
